import { OfficerBadge } from '@/components/common/OfficerBadge';
import { isOperatorCode, operatorCode } from '@/utils/operatorCode';
/**
 * MyCartsWidget — top 3 in-flight account-opening carts for the officer.
 *
 * Backed by `client.retail.carts.listMy()` (CartsApi.listMy). Some environments
 * still 501 on this route; treat 501 as a graceful "endpoint pending" empty
 * state — same convention as the legacy /rop dashboard.
 *
 * Click on a row → /rao/:cartId resumes the wizard. "See all" → /rao.
 *
 * Mine | My team | All (Bill 2026-10-16) replaces the "Show all (pilot)" checkbox: the shared switch in
 * teamScope.tsx, with the server deciding from the sign-in who is in My team and whether All is offered.
 */
import { identifyCart, unwrapCollection, personName, type ProductNameResolver } from '@/utils/cartLabel';
import { useConfig } from '@/contexts/ConfigContext';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ShoppingCart, ChevronRight, AlertTriangle } from 'lucide-react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useOfficerNameLookup, useOfficerEmailLookup } from '@/hooks/useOfficerCodeLookup';
import { useStaffProfiles, profileFor } from '@/hooks/useStaffProfiles';
import type { CartDto, TeamViewInfo } from '@retail-ops/shared-types';
import { ScopeSwitch, useScopeMode, useScopeViews, type ScopeTitles } from './teamScope';

const TITLES: ScopeTitles = { mine: 'My Carts', team: 'Team Carts', all: 'All Carts' };

// 12, not 3 (Bill 2026-09-11): the panels now fill the landing, so the list
// should too. Overflow scrolls inside the card.
const TOP_VISIBLE = 12;

interface State {
  carts: ReadonlyArray<CartDto>;
  totalCount: number;
  loading: boolean;
  pending: boolean;
  error: string | null;
}

const INITIAL: State = {
  carts: [],
  totalCount: 0,
  loading: true,
  pending: false,
  error: null,
};

function relativeTime(iso?: string): string | undefined {
  if (!iso) return undefined;
  try {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return undefined;
    const diffMs = Date.now() - t;
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    return `${days}d ago`;
  } catch {
    return undefined;
  }
}

/**
 * Best-effort primary-applicant name + product from the cart's own JSON blobs.
 * These are the SAME sources the New Acct cart card reads, so Home and New Acct
 * name a cart identically instead of disagreeing.
 */
/** Full detail for the hover tooltip — everyone on the cart, every product,
 *  status, and the cart id (last, for support). */
function cartTooltip(c: CartDto): string {
  const people = unwrapCollection(c.peopleJson, 'people').map(personName).filter(Boolean);
  const products = unwrapCollection(c.productsJson, 'products')
    .map((p) => {
      const o = (p ?? {}) as Record<string, unknown>;
      for (const k of ['displayName', 'name', 'productName', 'code']) {
        if (typeof o[k] === 'string' && (o[k] as string).trim()) return (o[k] as string).trim();
      }
      return '';
    })
    .filter(Boolean);

  const lines: string[] = [];
  lines.push(people.length ? `People: ${people.join(', ')}` : 'People: none added yet');
  lines.push(products.length ? `Products: ${products.join(', ')}` : 'Products: none selected yet');
  if (c.status) lines.push(`Status: ${c.status}`);
  if (c.pipelineStep) lines.push(`Step: ${c.pipelineStep.replace(/[_-]/g, ' ')}`);
  const updated = relativeTime(c.updatedAt);
  if (updated) lines.push(`Updated ${updated}`);
  // No cart id here (Bill 2026-09-11): a GUID identifies nothing to the
  // person reading it. Support can take the id from the URL.
  return lines.join('\n');
}

function summarizeCart(c: CartDto, resolveProduct?: ProductNameResolver): { title: string; meta: string } {
  const accountCount = c.accountIds?.length ?? 0;
  const cifCount = c.cifIds?.length ?? 0;

  // Title: identify the cart by WHO and WHAT — never the raw cartId GUID.
  // Bill 2026-07-27: Home showed "No accounts yet · draft" while New Acct
  // showed "John K. Wiggins · Minor My Student Checking" for the SAME cart.
  // Account count is useless as an identifier before commit (a draft has no
  // accounts BY DEFINITION, so every draft read identically), and pipeline
  // step describes progress, not identity. Fall back to the old label only
  // when the cart genuinely has no person on it yet.
  const stepLabel = c.pipelineStep?.replace(/[_-]/g, ' ') ?? c.status;
  const acctLabel =
    accountCount === 0
      ? 'No accounts yet'
      : accountCount === 1
        ? '1 account'
        : `${accountCount} accounts`;
  const { who, what, others } = identifyCart(c.peopleJson, c.productsJson, resolveProduct);
  // FALLBACK WORDING (Bill 2026-09-11). When a cart has no person on it the
  // old fallback read "No accounts yet · acct id gen" — two machine facts
  // stapled together, describing what the cart LACKS and which pipeline step
  // it stalled on. Neither tells an FSR what the row is or what to do with it,
  // and two empty carts rendered near-identically. Say what it actually is.
  const title = who
    ? [others > 0 ? `${who} +${others}` : who, what].filter(Boolean).join(' · ')
    : 'Untitled cart — no customer entered yet';

  // Meta: cifs + last update.
  const cifLabel =
    cifCount === 0
      ? undefined
      : cifCount === 1
        ? '1 customer'
        : `${cifCount} customers`;
  const updated = relativeTime(c.updatedAt);
  // The step and account count move DOWN here, where progress detail belongs,
  // instead of masquerading as the cart's name.
  const meta = [
    cifLabel,
    who ? undefined : acctLabel,
    who ? undefined : stepLabel,
    updated ? `updated ${updated}` : undefined,
  ]
    .filter(Boolean)
    .join(' · ');

  return { title, meta };
}

export function MyCartsWidget(): JSX.Element {
  const { user } = useAuth();
  const { config } = useConfig();
  // Mine | My team | All (Bill 2026-10-16). The server applies each view from the sign-in; a view it no longer
  // offers comes back as team.hasTeam=false and the panel drops to Mine.
  const views = useScopeViews();
  const [scope, setScope] = useScopeMode('rop.home.carts.scope', views);
  const resolveProduct: ProductNameResolver = (code) =>
    config?.products?.find((p) => p.code === code)?.name;
  const client = useApiClient();
  const navigate = useNavigate();
  // "Show all" lists other officers' carts: say whose each one is (Bill 2026-10-13).
  // Opening a cart never changes its owner - only Reassign or claiming an online cart does.
  const officerName = useOfficerNameLookup();
  const emailFor = useOfficerEmailLookup();
  const [s, setS] = useState<State>(INITIAL);

  useEffect(() => {
    const ctrl = new AbortController();
    client.retail.carts
      .listMy(scope === 'mine' ? undefined : { view: scope }, { signal: ctrl.signal })
      .then((res) => {
        if (ctrl.signal.aborted) return;
        if (scope !== 'mine' && (res as { team?: TeamViewInfo }).team?.hasTeam === false) { setScope('mine'); return; }
        const data = res.success && res.data ? res.data : [];
        // Newest-first ordering.
        const sorted = [...data].sort((a, b) => {
          const ta = new Date(a.updatedAt ?? a.createdAt ?? 0).getTime();
          const tb = new Date(b.updatedAt ?? b.createdAt ?? 0).getTime();
          return tb - ta;
        });
        setS({
          carts: sorted,
          totalCount: sorted.length,
          loading: false,
          pending: !res.success,
          error: null,
        });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        const msg = err instanceof Error ? err.message : '';
        const is501 = /\b(501|not[- ]implemented)\b/i.test(msg);
        setS({
          carts: [],
          totalCount: 0,
          loading: false,
          pending: is501,
          error: is501 ? null : msg || 'Unable to load carts.',
        });
      });
    return () => ctrl.abort();
  }, [client, scope]);

  const visible = s.carts.slice(0, TOP_VISIBLE);
  const profiles = useStaffProfiles(
    visible.map((c) => (c.assignedUser && c.assignedUser.includes('@') ? c.assignedUser : emailFor(c.officerCode))),
  );
  const remaining = Math.max(0, s.totalCount - visible.length);

  return (
    <section className="home-card home-card--carts" aria-label={TITLES[scope]} data-tour="home-my-carts">
      <header className="home-card__head">
        <div className="home-card__head-left">
          <ShoppingCart size={16} aria-hidden className="home-card__head-icon" />
          <h2 className="home-card__title">{TITLES[scope]}</h2>
          <span className="home-card__count">{s.loading ? '…' : s.totalCount}</span>
        </div>
        <div className="home-card__head-right">
          <Link to="/rao" className="home-card__see-all">
            See all <ChevronRight size={12} aria-hidden />
          </Link>
        </div>
      </header>
      <ScopeSwitch mode={scope} views={views} onChange={setScope} label="carts" />

      <div className="home-card__body">
        {s.loading ? (
          <ul className="home-skel">
            {[0, 1, 2].map((i) => (
              <li key={i} className="home-skel__row">
                <span className="home-skel__bar home-skel__bar--lg" />
                <span className="home-skel__bar home-skel__bar--sm" />
              </li>
            ))}
          </ul>
        ) : s.error ? (
          <div className="home-card__error">
            <AlertTriangle size={14} aria-hidden /> {s.error}
          </div>
        ) : visible.length === 0 ? (
          <div className="home-card__empty">
            <span className="home-card__empty-emoji" aria-hidden>✦</span>
            <p>
              {s.pending
                ? 'My-carts view comes online with the next deploy.'
                : scope === 'mine' ? 'No carts in flight — start a new application?'
                : scope === 'team' ? 'Nobody on your team has a cart in flight.' : 'No carts in flight anywhere.'}
            </p>
            {!s.pending && scope === 'mine' && (
              <button
                type="button"
                className="home-card__empty-cta"
                onClick={() => navigate('/rao')}
              >
                + New application
              </button>
            )}
          </div>
        ) : (
          <ul className="home-list">
            {visible.map((c) => {
              const { title, meta } = summarizeCart(c, resolveProduct);
              const code = isOperatorCode(c.officerCode) ? operatorCode(c.officerCode) : '';
              const upn = c.assignedUser && c.assignedUser.includes('@') ? c.assignedUser : emailFor(code);
              return (
                <li key={c.cartId} className="home-list__li home-list__li--person">
                  {code && (
                    <OfficerBadge code={code} upn={upn} profile={profileFor(profiles, upn)}
                      fallbackName={officerName(c.officerCode, c.assignedUser)}
                      reference={{ title, cartId: c.cartId }} />
                  )}
                  <button
                    type="button"
                    className="home-list__row"
                    onClick={() => navigate(`/rao/${encodeURIComponent(c.cartId)}`)}
                    // HOVER DETAIL (Bill 2026-09-11). Two lines is the right
                    // density for a Home tile, but it means everyone on the
                    // cart, the products, and the cart id are invisible. The
                    // tooltip carries the rest without spending layout on it.
                    title={cartTooltip(c)}
                  >
                    <span className="home-list__title">
                      {title}
                      {/* Officer's Core Director initials - same badge as the
                          claiming FSR on online carts, so authorship reads at
                          a glance (Bill 2026-09-11). */}
                    </span>
                    {meta && <span className="home-list__meta">{meta}</span>}
                    {scope !== 'mine' && (c.officerCode || c.assignedUser) && (
                      <span className="home-list__meta home-list__owner">
                        Owner: {profileFor(profiles, c.assignedUser && c.assignedUser.includes('@') ? c.assignedUser : emailFor(c.officerCode))?.name
                          || officerName(c.officerCode, c.assignedUser) || c.assignedUser || c.officerCode}
                        {isOperatorCode(c.officerCode) ? ` (${operatorCode(c.officerCode)})` : ''}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {remaining > 0 && !s.loading && (
        <footer className="home-card__foot">
          <Link to="/rao" className="home-card__foot-link">
            +{remaining} more open cart{remaining === 1 ? '' : 's'}
          </Link>
        </footer>
      )}
    </section>
  );
}
